import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { MobilePage, Icon } from '../../mobile';
import LeaderboardMiniTile from '../tiles/LeaderboardMiniTile';
import ShiftPickupTile from '../tiles/ShiftPickupTile';

// ============================================================
// Driver home on the phone (Sep 8 mobile rebuild).
// A driver opens the app in the truck for one of three reasons: is there
// a call, how am I doing this week, do I have a task. That is the whole
// screen: one primary action into the queue, three tiles, the shift
// pickup card when it applies, and the leaderboard underneath.
// Same data sources as the desktop driver dashboard tiles.
// ============================================================

function greeting(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return 'Morning';
  if (h < 17) return 'Afternoon';
  return 'Evening';
}
function formatLongDate(now = new Date()) {
  return now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

function Tile({ icon, color, title, sub, value, onClick }) {
  return (
    <div className="m-card" role="button" tabIndex={0} onClick={onClick}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16, cursor: 'pointer', borderRadius: 14 }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={icon} size={24} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{sub}</div>
      </div>
      {value != null && (
        <div style={{ fontSize: 28, fontWeight: 800, color, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      )}
      {value == null && <Icon name="chevron" size={20} color="#cbd5e1" />}
    </div>
  );
}

export default function DriverHomeMobile({ user }) {
  const navigate = useNavigate();
  const [queue, setQueue] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [tows, setTows] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const [q, t, d] = await Promise.all([
        api.getDashboardQueueStats().catch(() => null),
        api.getWorkflowItemCounts().catch(() => null),
        api.getDashboardDriverStats().catch(() => null),
      ]);
      if (!alive) return;
      setQueue(q); setTasks(t); setTows(d);
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const firstName = (user?.full_name || '').split(' ')[0] || 'there';
  const waiting = queue?.space_calls ?? null;
  const queueLabel = waiting == null ? 'Space Call Queue'
    : waiting === 0 ? 'Space Call Queue · none waiting'
      : `Space Call Queue · ${waiting} waiting`;

  return (
    <MobilePage title={`${greeting()}, ${firstName}`} subtitle={formatLongDate()}>
      <button type="button" className="m-btn m-btn-primary" style={{ marginBottom: 14 }} onClick={() => navigate('/dispatch')}>
        <Icon name="queue" size={22} color="#fff" /> {queueLabel}
      </button>

      <div className="m-list" style={{ marginBottom: 14 }}>
        <Tile icon="board" color="#3b82f6" title="Tows this week"
          sub={tows?.avg_resolution_minutes != null ? `Avg resolution ${tows.avg_resolution_minutes}m` : 'Your board is below'}
          value={tows ? String(tows.weekly_tows ?? 0) : '—'} onClick={() => navigate('/leaderboard')} />
        <Tile icon="tasks" color="#d97706" title="My tasks"
          sub={tasks?.active ? `${tasks.active} open` : 'Nothing assigned'}
          value={tasks ? String(tasks.active ?? 0) : '—'} onClick={() => navigate('/workflow')} />
        <Tile icon="alert" color="#dc2626" title="Report an issue" sub="Truck, property, or task" onClick={() => navigate('/workflow/report-issue')} />
        <Tile icon="sign" color="#0891b2" title="Report a sign problem" sub="Missing, damaged, or wrong sign" onClick={() => navigate('/sign-report')} />
        <Tile icon="key" color="#4f46e5" title="Can't get through a gate" sub="Code, GateWise, or clicker not working" onClick={() => navigate('/gate-report')} />
      </div>

      <div style={{ marginBottom: 14 }}><ShiftPickupTile /></div>

      <LeaderboardMiniTile groupName="$sub_team" title="Driver Leaderboard" />
    </MobilePage>
  );
}
